import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle } from "./ui/dialog";
import { useCategories } from "@/context/SiteContentContext";
import { usePartner } from "@/context/PartnerContext";
import { Package, Boxes, FlaskConical, ShoppingBag, Bitcoin, CheckCircle2, HelpCircle, Layers, QrCode, Truck, ShieldCheck, Search, Droplets, Gauge } from "lucide-react";

// small looping ticker
const useTick = (ms) => {
  const [t, setT] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setT((v) => v + 1), ms);
    return () => clearInterval(id);
  }, [ms]);
  return t;
};

const COLORS = ["#e2497a", "#f4a63b", "#5cc9a7", "#7b6cf0", "#e85d5d", "#3bb0f4", "#f26fb2", "#4fd1c5"];

const Panel = ({ children }) => (
  <div className="relative flex h-40 items-center justify-center overflow-hidden rounded-lg border border-border bg-background p-4">{children}</div>
);

// ---- mini previews ----
const SIZES = ["60ml", "100ml", "250ml", "500ml", "1L", "5L"];
const SizeAnim = () => {
  const t = useTick(900);
  const active = t % SIZES.length;
  return (
    <Panel>
      <div className="grid grid-cols-3 gap-1.5 sm:grid-cols-6">
        {SIZES.map((s, i) => (
          <motion.div key={s} animate={{ scale: active === i ? 1.1 : 1 }} transition={{ type: "spring", stiffness: 300, damping: 18 }}
            className={`flex h-14 flex-col items-center justify-center rounded border text-[10px] font-bold ${active === i ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground"}`}>{s}</motion.div>
        ))}
      </div>
    </Panel>
  );
};

const QTY_STEPS = [{ q: 1, p: "£17.50" }, { q: 5, p: "£16.00" }, { q: 10, p: "£15.00" }, { q: 20, p: "£14.00" }];
const QtyAnim = () => {
  const t = useTick(1100);
  const s = QTY_STEPS[t % QTY_STEPS.length];
  return (
    <Panel>
      <div className="text-center">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Bottles</div>
        <motion.div key={s.q} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="font-heading text-4xl font-black">{s.q}</motion.div>
        <div className="mono mt-1 text-sm">
          <motion.span key={s.p} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-bold text-primary">{s.p}</motion.span>
          <span className="text-muted-foreground"> / bottle</span>
        </div>
        <div className="mono mt-1 text-[9px] uppercase tracking-widest text-primary">More bottles · lower price</div>
      </div>
    </Panel>
  );
};

const VOLS = ["1L", "2L", "3L", "4L", "5L"];
const VolumeAnim = () => {
  const t = useTick(900);
  const active = t % VOLS.length;
  return (
    <Panel>
      <div className="grid grid-cols-5 gap-1.5">
        {VOLS.map((volume, i) => (
          <motion.div key={volume} animate={{ scale: active === i ? 1.1 : 1 }} transition={{ type: "spring", stiffness: 300, damping: 18 }}
            className={`flex h-14 flex-col items-center justify-center rounded border font-heading text-base font-black ${active === i ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card"}`}>{volume}</motion.div>
        ))}
      </div>
    </Panel>
  );
};

// Choose box style — the first thing you pick
const ChooseStyleAnim = () => {
  const cats = useCategories();
  const STYLES = [
    { title: cats.build.title, sub: "Any online sizes", icon: Package },
    { title: cats.deals.title, sub: "Mixed sizes · 5% off", icon: Layers },
  ];
  const t = useTick(1400);
  const active = t % STYLES.length;
  return (
    <Panel>
      <div className="grid w-full max-w-[260px] grid-cols-2 gap-2">
        {STYLES.map((s, i) => {
          const Icon = s.icon;
          const on = active === i;
          return (
            <motion.div key={s.title} animate={{ borderColor: on ? "hsl(266 72% 66%)" : "hsl(240 4% 26%)", backgroundColor: on ? "hsla(266,72%,66%,0.14)" : "hsla(0,0%,0%,0)" }}
              className="flex flex-col items-center gap-1 rounded border p-3 text-center">
              <Icon className={`h-6 w-6 ${on ? "text-primary" : "text-muted-foreground"}`} />
              <span className="text-[11px] font-bold leading-tight">{s.title}</span>
              <span className="mono text-[8px] uppercase tracking-widest text-muted-foreground">{s.sub}</span>
            </motion.div>
          );
        })}
      </div>
    </Panel>
  );
};

const PACKS = [["2×250 + 5×100", "£185.25"], ["1×500 + 2×250", "£166.25"], ["4×100 + 10×60", "£198.55"]];
const PackAnim = () => {
  const t = useTick(1000);
  const active = t % PACKS.length;
  return (
    <Panel>
      <div className="w-full max-w-[240px] space-y-1.5">
        {PACKS.map(([label, price], i) => (
          <motion.div key={label} animate={{ borderColor: active === i ? "hsl(266 72% 66%)" : "hsl(240 4% 26%)", backgroundColor: active === i ? "hsla(266,72%,66%,0.14)" : "hsla(0,0%,0%,0)" }}
            className="flex items-center justify-between rounded border px-3 py-2 text-xs">
            <span>{label}</span><span className="mono font-bold">{price}</span>
          </motion.div>
        ))}
      </div>
    </Panel>
  );
};

const FillAnim = () => {
  const t = useTick(480);
  const filled = t % 11; // 0..10 (fill 8 then pause then reset)
  return (
    <Panel>
      <div className="grid grid-cols-4 gap-1.5">
        {Array.from({ length: 8 }).map((_, i) => {
          const on = i < filled;
          return (
            <motion.div key={i} animate={{ backgroundColor: on ? COLORS[i % COLORS.length] : "hsl(240 6% 14%)", borderColor: on ? COLORS[i % COLORS.length] : "hsl(240 4% 26%)" }}
              transition={{ duration: 0.3 }} className="flex h-9 w-9 items-center justify-center rounded border">
              {!on && <span className="text-[10px] font-bold text-muted-foreground">+</span>}
            </motion.div>
          );
        })}
      </div>
    </Panel>
  );
};

const BASES = ["CO Base", "DH Base"];
const STRENGTHS = [
  { label: "Standard", note: "as-is" },
  { label: "×2 Double", note: "+40%" },
  { label: "×3 Triple", note: "+75%" },
];
const BaseStrengthAnim = () => {
  const t = useTick(1100);
  const baseIdx = Math.floor(t / STRENGTHS.length) % BASES.length;
  const strIdx = t % STRENGTHS.length;
  return (
    <Panel>
      <div className="w-full max-w-[250px] space-y-3">
        <div>
          <div className="mono mb-1 flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-muted-foreground">
            <Droplets className="h-3 w-3 text-primary" /> Base
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {BASES.map((b, i) => (
              <motion.div key={b} animate={{ borderColor: baseIdx === i ? "hsl(266 72% 66%)" : "hsl(240 4% 26%)", backgroundColor: baseIdx === i ? "hsla(266,72%,66%,0.14)" : "hsla(0,0%,0%,0)" }}
                className="rounded border px-2 py-1.5 text-center text-[11px] font-bold">{b}</motion.div>
            ))}
          </div>
        </div>
        <div>
          <div className="mono mb-1 flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-muted-foreground">
            <Gauge className="h-3 w-3 text-primary" /> Strength
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            {STRENGTHS.map((s, i) => (
              <motion.div key={s.label} animate={{ borderColor: strIdx === i ? "hsl(266 72% 66%)" : "hsl(240 4% 26%)", backgroundColor: strIdx === i ? "hsla(266,72%,66%,0.14)" : "hsla(0,0%,0%,0)" }}
                className="flex flex-col items-center rounded border px-1 py-1.5 text-center">
                <span className="text-[11px] font-bold leading-tight">{s.label}</span>
                <span className="mono text-[8px] uppercase tracking-widest text-primary">{s.note}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </Panel>
  );
};


const CartAnim = () => {
  const t = useTick(1800);
  return (
    <Panel>
      <motion.div key={t} initial={{ x: 36, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ type: "spring", stiffness: 200, damping: 20 }}
        className="w-full max-w-[230px] rounded border border-border bg-card p-3">
        <div className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded bg-primary/20"><ShoppingBag className="h-4 w-4 text-primary" /></div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-xs font-bold">10 × 100ml Box</div>
            <div className="mono text-[10px] text-muted-foreground">10 flavours · your mix</div>
          </div>
        </div>
        <div className="mt-2 flex justify-between border-t border-border pt-2 text-xs">
          <span className="text-muted-foreground">Box total</span><span className="mono font-bold text-primary">£180.00</span>
        </div>
      </motion.div>
    </Panel>
  );
};

const COINS = ["BTC", "LTC"];
const CoinAnim = () => {
  const t = useTick(800);
  const active = t % COINS.length;
  return (
    <Panel>
      <div className="flex flex-wrap justify-center gap-1.5">
        {COINS.map((c, i) => (
          <motion.div key={c} animate={{ scale: active === i ? 1.12 : 1 }} transition={{ type: "spring", stiffness: 300, damping: 16 }}
            className={`rounded-full border px-3 py-1.5 text-xs font-bold ${active === i ? "border-primary bg-primary text-primary-foreground" : "border-border"}`}>{c}</motion.div>
        ))}
      </div>
    </Panel>
  );
};

const QR_ON = new Set([0, 1, 2, 4, 5, 7, 9, 10, 12, 14, 15, 17, 19, 20, 22, 23, 24, 3, 11, 13, 21]);
const PayAnim = () => {
  const t = useTick(50);
  const pct = (t * 2.5) % 100;
  return (
    <Panel>
      <div className="flex items-center gap-4">
        <div className="grid grid-cols-5 gap-0.5 rounded bg-white p-1.5">
          {Array.from({ length: 25 }).map((_, i) => (
            <div key={i} className={`h-2.5 w-2.5 ${QR_ON.has(i) ? "bg-black" : "bg-transparent"}`} />
          ))}
        </div>
        <div className="min-w-0 flex-1">
          <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Send exact amount</div>
          <div className="mono truncate text-[11px]">bc1q7x…k9f2</div>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded bg-muted">
            <motion.div animate={{ width: `${pct}%` }} transition={{ ease: "linear" }} className="h-full bg-primary" />
          </div>
          <div className="mono mt-1 text-[9px] text-primary">Waiting for payment…</div>
        </div>
      </div>
    </Panel>
  );
};

const DoneAnim = () => {
  const t = useTick(1800);
  return (
    <Panel>
      <motion.div key={t} initial={{ scale: 0, rotate: -18 }} animate={{ scale: 1, rotate: 0 }} transition={{ type: "spring", stiffness: 200, damping: 12 }}
        className="flex flex-col items-center gap-2 text-primary">
        <CheckCircle2 className="h-14 w-14" />
        <span className="mono text-[10px] uppercase tracking-widest">Order confirmed · we ship it</span>
      </motion.div>
    </Panel>
  );
};

const PrivacyAnim = () => {
  const t = useTick(1600);
  const mode = t % 2; // 0 = PGP note, 1 = temp.pm link
  return (
    <Panel>
      <div className="w-full max-w-[240px] rounded border border-border bg-card p-3">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Delivery details · encrypted</span>
        </div>
        <motion.div key={mode} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          className="mono mt-2 truncate rounded border border-border bg-background px-2 py-1.5 text-[10px] text-primary">
          {mode === 0 ? "-----BEGIN PGP MESSAGE----- hQEMA…" : "temp.pm/#a1B2c3…self-destructs"}
        </motion.div>
        <div className="mono mt-2 text-[8px] uppercase tracking-widest text-muted-foreground">Never stored in the clear</div>
      </div>
    </Panel>
  );
};

const TrackAnim = () => {
  const t = useTick(1500);
  const stage = t % 3;
  const STATUS = ["Awaiting payment", "Being prepared", "Shipped"];
  return (
    <Panel>
      <div className="w-full max-w-[240px] rounded border border-border bg-card p-3">
        <div className="flex items-center justify-between">
          <span className="mono text-xs font-bold"><SampleOrderNumber /></span>
          <span className="mono rounded-full bg-primary/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-primary">PIN 4KQ7P2</span>
        </div>
        <div className="mono mt-2 flex items-center gap-1.5 text-[9px] uppercase tracking-widest text-muted-foreground">
          <Search className="h-3 w-3 text-primary" /> Status
        </div>
        <motion.div key={stage} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          className="mt-1 mono text-sm font-bold text-primary">{STATUS[stage]}</motion.div>
        <div className="mono mt-1 text-[8px] uppercase tracking-widest text-muted-foreground">No account · no email needed</div>
      </div>
    </Panel>
  );
};

const SHIP = [["United Kingdom", "£5.00"], ["Ireland", "£12.00"], ["Germany", "£15.00"], ["United States", "£20.00"]];
const BasketShipAnim = () => {
  const t = useTick(1400);
  const [country, ship] = SHIP[t % SHIP.length];
  return (
    <Panel>
      <div className="w-full max-w-[240px] rounded border border-border bg-card p-3">
        <div className="mono text-[9px] uppercase tracking-widest text-muted-foreground">Ship to</div>
        <motion.div key={country} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
          className="mt-1 flex items-center justify-between rounded border border-border px-2 py-1.5 text-[11px]">
          <span className="truncate">{country}</span><span className="text-muted-foreground">▾</span>
        </motion.div>
        <div className="mt-2 flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 text-muted-foreground"><Truck className="h-3.5 w-3.5" /> Shipping</span>
          <motion.span key={ship} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mono font-bold text-primary">{ship}</motion.span>
        </div>
        <div className="mono mt-1 text-[8px] uppercase tracking-widest text-primary">Calculated before you pay</div>
      </div>
    </Panel>
  );
};

const chooseStep = (cats) => ({ icon: Layers, anim: ChooseStyleAnim, title: "Choose how to order", text: `Choose ${cats.build.title} for complete freedom across all online bottle sizes, or ${cats.deals.title} for the familiar ready-mixed 1–5L presets.` });

const BASE_STRENGTH_STEP = {
  icon: Droplets,
  anim: BaseStrengthAnim,
  title: "Choose the base & strength for each bottle",
  text: "For every bottle you also pick two things. Base: choose which base it's mixed in — CO Base or DH Base. Strength: choose Standard, ×2 Double or ×3 Triple. Set them per bottle, or set one choice and apply it to all.",
};

const SHOP_STEPS = [
  { icon: Package, anim: SizeAnim, title: "Build inside each size tile", text: "Set the quantity and that tile expands with one flavour, base and strength menu for every bottle. You can also set one choice and apply it to all bottles of that size." },
  BASE_STRENGTH_STEP,
  { icon: ShoppingBag, anim: CartAnim, title: "Add your box", text: "Review your choices and add to basket." },
];

const CUSTOM_STEPS = [
  { icon: Layers, anim: VolumeAnim, title: "Pick your total volume", text: "Choose how much you want overall — 1 to 5 litres." },
  { icon: Boxes, anim: PackAnim, title: "Choose a mixed multipack", text: "Pick a ready-balanced mix of bottle sizes that adds up to your volume. Mixed-size boxes are 5% cheaper than buying the same bottles separately." },
  { icon: FlaskConical, anim: FillAnim, title: "Fill each bottle", text: "Tap a tile, choose base, strength and flavour. “+1 / +5 / +All” fills the rest instantly, and the little × clears a bottle if you change your mind." },
  BASE_STRENGTH_STEP,
  { icon: ShoppingBag, anim: CartAnim, title: "Add your multipack", text: "Review your mix and drop it into the basket." },
];

const CHECKOUT_STEPS = [
  { icon: Truck, anim: BasketShipAnim, title: "Check your basket & shipping", text: "In the basket, pick your delivery country to see the exact shipping cost and final total before you pay." },
  { icon: ShieldCheck, anim: PrivacyAnim, title: "Add your delivery details — privately", text: "This is the key privacy step, and it's in your hands — we never store your address on our site, so you send it to us encrypted. Preferred: scramble your name and address with our PGP public key and paste the encrypted block in at checkout. Prefer simpler? Type your address on temp.pm and paste the one-time link instead — we open it once to pack your order, then it self-destructs." },
  { icon: Bitcoin, anim: CoinAnim, title: "Pick a coin & checkout", text: "Choose Bitcoin or Litecoin and pay on our secure payment page. No card, no account, no KYC." },
  { icon: QrCode, anim: PayAnim, title: "Send the payment", text: "Scan the QR or copy the address and send the exact amount from your wallet." },
  { icon: CheckCircle2, anim: DoneAnim, title: "Order confirmed", text: "As soon as the payment lands, your order is confirmed and we ship it. You'll get a short tracking PIN to keep." },
  { icon: Search, anim: TrackAnim, title: "Track it any time", text: "Every order gets a 6-character tracking PIN. Enter your order number and PIN on the Track page to see live status — no account, email or address required." },
];

const SampleOrderNumber = () => { const { partner } = usePartner(); return `${partner?.order_prefix || "DL"}-9F2K7A1C`; };

export const HowItWorksSteps = ({ flow = "shop" }) => {
  const cats = useCategories();
  const steps = [chooseStep(cats), ...(flow === "custom" ? CUSTOM_STEPS : SHOP_STEPS), ...CHECKOUT_STEPS];
  return (
    <div className="space-y-8" data-testid="hiw-steps">
      {steps.map((s, idx) => {
        const Icon = s.icon;
        const Anim = s.anim;
        return (
          <div key={idx} data-testid={`hiw-step-${idx}`} className="grid grid-cols-1 items-center gap-4 md:grid-cols-2">
            <div className={idx % 2 === 1 ? "md:order-2" : ""}>
              <div className="flex items-center gap-2">
                <span className="mono flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-black text-primary-foreground">{idx + 1}</span>
                <Icon className="h-4 w-4 text-primary" />
                <h3 className="font-heading text-lg font-bold">{s.title}</h3>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
            </div>
            <div className={idx % 2 === 1 ? "md:order-1" : ""}><Anim /></div>
          </div>
        );
      })}
    </div>
  );
};

export const HowItWorksModal = ({ flow = "shop", label = "How it works" }) => (
  <Dialog>
    <DialogTrigger asChild>
      <button data-testid="how-it-works-btn" className="mono inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] text-muted-foreground transition-colors hover:border-primary hover:text-primary">
        <HelpCircle className="h-3.5 w-3.5" /> {label}
      </button>
    </DialogTrigger>
    <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto" data-testid="how-it-works-modal">
      <DialogHeader>
        <DialogTitle className="font-heading text-2xl font-black tracking-tight">How ordering works</DialogTitle>
      </DialogHeader>
      <HowItWorksSteps flow={flow} />
    </DialogContent>
  </Dialog>
);
